import React, { useState, useEffect, useRef } from 'react';

import UIStore from '@/stores/UIStore';
import UserStore from 'stores/UserStore';
import { CustomResponsiveTable } from '../../components/common/SimpleResponsiveTable';

import { Button, Empty, Input, Card, notification } from 'antd';

import { ColumnProps, TableProps, TablePaginationConfig } from 'antd/es/table';
import { CreditCardOutlined, EyeOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import _ from 'lodash';

import InvoiceStore from 'stores/InvoiceStore';

import { useHistory } from 'react-router-dom';
import { IInvoice } from 'types/types';
import { Price } from 'components/Display';
import { ListPageHeader, StatusChip } from 'components/ListPageHeader';
import { ErrorRender } from '@partsbadger/library';
import { Tag } from 'antd';
import DateRange from 'components/SalesOrders/DropDowns/DateRange';
import { buildInvoiceQueryParams, normalizeDateRange } from './helpers';

const STATUS_CHIPS: StatusChip[] = [
    { label: 'All', value: undefined },
    { label: 'Unpaid', value: 'Unpaid' },
    { label: 'Paid', value: 'Paid' },
];

export const InvoiceList: React.FC = () => {
    const [data, setData] = useState<IInvoice[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 30 });
    const [search, setSearch] = useState<string>();
    const [statusFilter, setStatusFilter] = useState<string | undefined>();
    const [invoiceDates, setInvoiceDates] = useState<[string, string] | undefined>();
    const [dueDates, setDueDates] = useState<[string, string] | undefined>();
    const history = useHistory();

    const debouncedSearchRef = useRef(
        _.debounce((value: string) => {
            setSearch(value || undefined);
        }, 400)
    );

    const fetchInvoices = async (
        newPagination?: TablePaginationConfig,
        sorter?: { field?: string; order?: string }
    ) => {
        setLoading(true);

        const params = buildInvoiceQueryParams({
            pagination: newPagination,
            search,
            statusFilter,
            invoiceDates,
            dueDates,
            sorter,
        });

        try {
            const response = await InvoiceStore.getInvoiceList(params);
            setPagination(prev => ({
                ...prev,
                total: response.count,
                current: newPagination?.current,
            }));
            setData(response.results);
        } catch (error: any) {
            notification.error({
                duration: 10,
                message: 'Error',
                description: <ErrorRender error={error} />,
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        UIStore.setSelectedKey(['invoices']);
        UIStore.setDocumentTitle('Invoices');

        if (UserStore.isVerified) {
            const nextPagination: TablePaginationConfig = {
                ...pagination,
                current: 1,
                pageSize: pagination?.pageSize ?? 30,
            };
            setPagination(prev => ({
                ...prev,
                current: 1,
                pageSize: prev?.pageSize ?? 30,
            }));
            fetchInvoices(nextPagination);
        }
    }, [search, statusFilter, invoiceDates, dueDates]);

    const handleTableChange: TableProps<IInvoice>['onChange'] = (newPagination, _filters, sorter) => {
        setPagination(newPagination);
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        fetchInvoices(newPagination, s?.field ? { field: s.field as string, order: s.order as string } : undefined);
    };

    const columns: ColumnProps<IInvoice>[] = [
        {
            title: 'Invoice Number',
            dataIndex: 'name',
        },
        {
            title: 'Status',
            dataIndex: 'invoice_status',
            render: (_text, record) => (
                <Tag color={record.invoice_status === 'Unpaid' ? 'red' : 'green'}>{record.invoice_status}</Tag>
            ),
        },
        {
            title: 'Purchase Order',
            dataIndex: 'purchase_order',
        },
        {
            title: 'Date',
            dataIndex: 'invoice_date',
            filteredValue: invoiceDates ?? null,
            filterDropdown: (
                <DateRange
                    current={invoiceDates}
                    search={(min, max) => {
                        setInvoiceDates(normalizeDateRange(min, max));
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            filteredValue: dueDates ?? null,
            filterDropdown: (
                <DateRange
                    current={dueDates}
                    search={(min, max) => {
                        setDueDates(normalizeDateRange(min, max));
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
        },
        {
            title: 'Grand Total',
            dataIndex: 'total_with_shipping',
            render: (_text, record) => <Price style={{ fontSize: '14px' }} value={record.total_with_shipping} />,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_text, record) =>
                record.invoice_status === 'Unpaid' ? (
                    <Button
                        type="primary"
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            history.push(`/my-account/details-invoice/${record.id}`);
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                        <CreditCardOutlined />
                        Pay Now
                    </Button>
                ) : (
                    <Button
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            history.push(`/my-account/details-invoice/${record.id}`);
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                        <EyeOutlined />
                        View Details
                    </Button>
                ),
        },
    ];

    const emptyTable = () => (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
                data.length === 0 && !search && !statusFilter ? (
                    <span style={{ color: '#000' }}>
                        Place an order with PartsBadger and see the status of your invoices here!
                    </span>
                ) : (
                    <span>Could not find anything with your search</span>
                )
            }
        />
    );

    const hasActiveFilters = !!statusFilter || !!search || !!invoiceDates || !!dueDates;

    return (
        <>
            <ListPageHeader
                title="Invoices"
                subtitle="Manage and track your invoices and payments"
                searchNode={
                    <Input
                        allowClear
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        placeholder="Search by invoice number or PO..."
                        size="large"
                        className="w-full"
                        onChange={e => debouncedSearchRef.current(e.target.value)}
                    />
                }
                chips={STATUS_CHIPS}
                activeChip={statusFilter}
                onChipChange={setStatusFilter}
                dates={undefined}
                onDatesChange={() => undefined}
                showDateFilter={false}
                totalCount={pagination.total ?? 0}
                entityName="invoices"
                hasActiveFilters={hasActiveFilters}
                onClearFilters={() => {
                    setStatusFilter(undefined);
                    setInvoiceDates(undefined);
                    setDueDates(undefined);
                    setSearch(undefined);
                }}
            />

            <div className="container">
                <Card>
                    <CustomResponsiveTable
                        columns={columns}
                        loading={loading}
                        data={data}
                        pagination={pagination}
                        onChange={handleTableChange}
                        emptyTable={emptyTable}
                        onRow={(record: IInvoice) => ({
                            onClick: () => history.push(`/my-account/details-invoice/${record.id}`),
                            style: { cursor: 'pointer' },
                        })}
                    />
                </Card>
            </div>
        </>
    );
};
